import { readonly, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { compare as createPatch } from 'fast-json-patch';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { useAuthStore } from '@/stores/auth';
import {
    BODY_POSITION_EXTENSION_URL,
    BODY_POSITIONS,
    BP_COMPONENT_CODINGS,
    OBSERVATION_CATEGORY_SYSTEM,
    PANEL_CODING,
    PANEL_PROFILE,
    UCUM_SYSTEM,
    VITAL_SIGNS_CATEGORY,
    VITALS,
    VITALS_BY_KEY,
    VITALS_INTERPRETATION_SYSTEM,
} from '@/common/vitalsConstants';
import {
    ADULT_BMI_AGE,
    ageInYears,
    bmiCategory,
    bpCategory,
    calculateBmi,
    calculatePercentOf,
    feetInchesToInches,
    interpretationFor,
    isBlank,
    isOrthostaticDrop,
    orthostaticReference,
    referenceRangeFor,
} from '@/common/vitalsRules';
import { vitalKeyFor } from '@/common/vitalsDisplay';

/**
 * Vitals workflow: the only place vitals FHIR resources are written or queried.
 *
 * A set is one panel Observation (LOINC 85353-1) whose hasMember lists one Observation per reading,
 * all written in one transaction with client-assigned ids so siblings can reference each other. An edit
 * amends, adds and clears readings and amends the panel (status amended, ifMatch on the version as read),
 * with a Provenance per change, all in one transaction. A delete removes the whole set and its Provenance.
 * Everything taken and returned is plain JSON in Aidbox native format, not fhirworks models.
 *
 * Must not import useQuestionnaireFlow, useEncounterFlow or useTaskFlow: they import this.
 */

// Goes up after every successful write, shared by every caller, so any surface showing vitals can reload.
const revision = ref(0);

const DATA_OPERATION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
// One page for every set across a group note's participants: a group holds a handful of people.
const NOTE_PANELS_FETCH_COUNT = 1000;

export class VitalsConflictError extends Error {
    constructor(observationId, cause) {
        super(`Vitals reading ${observationId} was changed by someone else since it was read`);
        this.name = 'VitalsConflictError';
        this.observationId = observationId;
        this.cause = cause;
    }
}

const toPlain = (resource) => (resource && typeof resource.toJSON === 'function' ? resource.toJSON() : resource);
const clone = (value) => JSON.parse(JSON.stringify(value));
const referenceId = (reference) => reference?.id || reference?.reference?.split('/')[1];
const observationReference = (id) => ({ id, resourceType: 'Observation' });

function isVitalSign(observation) {
    return (observation?.category || []).some((category) =>
        (category?.coding || []).some((coding) => coding?.code === 'vital-signs' && (!coding.system || coding.system === OBSERVATION_CATEGORY_SYSTEM)),
    );
}

const isPanel = (observation) => (observation?.code?.coding || []).some((coding) => coding?.code === PANEL_CODING.code);

function toNumber(value, label) {
    const number = Number(value);
    if (!Number.isFinite(number)) throw new Error(`${label} must be a number`);
    return number;
}

// An entered value as a number (inches for height) or { systolic, diastolic }; undefined when blank.
function normalizeReading(key, value) {
    const vital = VITALS_BY_KEY[key];
    if (vital.kind === 'bloodPressure') {
        if (isBlank(value?.systolic) && isBlank(value?.diastolic)) return undefined;
        if (isBlank(value?.systolic) || isBlank(value?.diastolic)) throw new Error(`${vital.label} needs both a systolic and a diastolic number`);
        return { systolic: toNumber(value.systolic, `${vital.label} systolic`), diastolic: toNumber(value.diastolic, `${vital.label} diastolic`) };
    }
    if (key === 'height' && value !== null && typeof value === 'object') {
        if (isBlank(value.feet) && isBlank(value.inches)) return undefined;
        return feetInchesToInches(toNumber(value.feet ?? 0, 'Height feet'), toNumber(value.inches ?? 0, 'Height inches'));
    }
    if (isBlank(value)) return undefined;
    return toNumber(value, vital.label);
}

// Calculated vitals always come from the inputs in the same set, never from the caller.
function withCalculated(values) {
    const result = { ...values };
    VITALS.filter((vital) => vital.calculatedFrom).forEach((vital) => {
        const inputs = vital.calculatedFrom.map((key) => values[key]);
        const calculated = vital.key === 'bmi' ? calculateBmi(...inputs) : calculatePercentOf(...inputs);
        if (calculated === null) {
            delete result[vital.key];
        } else {
            result[vital.key] = calculated;
        }
    });
    return result;
}

const quantity = (vital, value) => ({ Quantity: { value, unit: vital.unit.display, system: UCUM_SYSTEM, code: vital.unit.code } });

const bodyPositionExtension = (position) => ({ url: BODY_POSITION_EXTENSION_URL, value: { CodeableConcept: { coding: [BODY_POSITIONS[position]] } } });

// The sibling a standing blood pressure is compared against, when the comparison shows a drop.
function orthostaticComparison(values) {
    if (!values.bpStanding) return null;
    const referenceKey = orthostaticReference({ bpLying: values.bpLying, bpSitting: values.bpSitting });
    return referenceKey && isOrthostaticDrop(values.bpStanding, values[referenceKey]) ? referenceKey : null;
}

function interpretationForReading(key, values, age) {
    if (key === 'bmi') return interpretationFor(bmiCategory(values.bmi, age));
    if (VITALS_BY_KEY[key].kind !== 'bloodPressure') return [];
    const drop = key === 'bpStanding' && !!orthostaticComparison(values);
    return interpretationFor(bpCategory(values[key].systolic, values[key].diastolic), { orthostaticDrop: drop });
}

function readValue(key, observation) {
    if (VITALS_BY_KEY[key]?.kind === 'bloodPressure') {
        const part = (code) => (observation?.component || []).find((component) => component?.code?.coding?.some((coding) => coding?.code === code))?.value?.Quantity?.value;
        const systolic = part(BP_COMPONENT_CODINGS.systolic.code);
        const diastolic = part(BP_COMPONENT_CODINGS.diastolic.code);
        return isBlank(systolic) || isBlank(diastolic) ? undefined : { systolic, diastolic };
    }
    return observation?.value?.Quantity?.value;
}

// Resources the server returned from a write, keyed by id. Raw JSON from originalData when present.
function savedById(response) {
    const raw = response?.originalData?.entry?.map((entry) => entry?.resource) || (Array.isArray(response?.data) ? response.data : []);
    return Object.fromEntries(
        raw
            .map(toPlain)
            .filter((resource) => resource?.id)
            .map((resource) => [resource.id, resource]),
    );
}

const transactionBundle = (entry) => ({ resourceType: 'Bundle', type: 'transaction', entry });

// The fields every Observation in a set shares. `links` carries a note's encounter and derivedFrom.
const baseObservation = (id, profile, { subject, effectiveDateTime, performer, links }) => ({
    resourceType: 'Observation',
    id,
    meta: { profile: [profile] },
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    subject,
    effective: { dateTime: effectiveDateTime },
    performer: [performer],
    ...clone(links),
});

// A new reading for `key` from the set's values; `ids` holds every reading's id by key, for the orthostatic sibling reference.
function buildReading(key, values, { age, baseResource, ids }) {
    const vital = VITALS_BY_KEY[key];
    const reading = { ...baseResource(ids[key], vital.profile), code: { coding: vital.codings } };

    if (vital.kind === 'bloodPressure') {
        const ranges = referenceRangeFor(key);
        reading.component = ['systolic', 'diastolic'].map((part) => ({
            code: { coding: vital.componentCodings[part] },
            value: quantity(vital, values[key][part]),
            referenceRange: ranges[part],
        }));
    } else {
        reading.value = quantity(vital, values[key]);
    }

    const interpretation = interpretationForReading(key, values, age);
    if (interpretation.length) reading.interpretation = interpretation;
    if (key === 'bmi') reading.referenceRange = referenceRangeFor('bmi');
    const comparedWith = orthostaticComparison(values);
    if (key === 'bpStanding' && comparedWith) {
        reading.derivedFrom = [...(reading.derivedFrom || []), observationReference(ids[comparedWith])];
    }
    if (vital.position) reading.extension = [bodyPositionExtension(vital.position)];

    return clone(reading);
}

async function createSet({ patient, effectiveDateTime, readings = {}, note, questionnaireResponse } = {}) {
    if (!patient?.id) throw new Error('Vitals: a patient is required to record a set');
    if (!effectiveDateTime) throw new Error('Vitals: the time taken is required to record a set');

    const entered = {};
    VITALS.filter((vital) => !vital.calculatedFrom).forEach((vital) => {
        const value = normalizeReading(vital.key, readings?.[vital.key]);
        if (value !== undefined) entered[vital.key] = value;
    });
    const values = withCalculated(entered);
    const keys = VITALS.map((vital) => vital.key).filter((key) => values[key] !== undefined);
    if (!keys.length) throw new Error('Vitals: enter at least one reading');

    const ids = Object.fromEntries(keys.map((key) => [key, uuidv4()]));
    const panelId = uuidv4();
    const age = ageInYears(patient.birthDate, effectiveDateTime);
    const performer = clone(useAuthStore().aidboxPersonReference || {});

    const noteLinks = {};
    if (questionnaireResponse?.id) {
        if (questionnaireResponse.encounter?.id) {
            noteLinks.encounter = { id: questionnaireResponse.encounter.id, resourceType: questionnaireResponse.encounter.resourceType || 'Encounter' };
        }
        noteLinks.derivedFrom = [{ id: questionnaireResponse.id, resourceType: 'QuestionnaireResponse' }];
    }

    const baseResource = (id, profile) => baseObservation(id, profile, { subject: { id: patient.id, resourceType: 'Patient' }, effectiveDateTime, performer, links: noteLinks });

    const readingResources = {};
    keys.forEach((key) => {
        readingResources[key] = buildReading(key, values, { age, baseResource, ids });
    });

    const panel = { ...baseResource(panelId, PANEL_PROFILE), code: { coding: [PANEL_CODING] }, hasMember: keys.map((key) => observationReference(ids[key])) };
    if (!isBlank(note)) panel.note = [{ text: note.trim() }];

    const entry = [panel, ...keys.map((key) => readingResources[key])].map((resource) => ({ request: { method: 'POST', url: '/Observation' }, resource: clone(resource) }));

    let response;
    try {
        response = await $httpFhirApi.post('/', transactionBundle(entry));
    } catch (error) {
        console.error('[useVitalsFlow] createSet transaction failed', { patientId: patient.id, panelId, readingIds: ids, error });
        throw error;
    }

    const saved = savedById(response);
    revision.value++;
    return {
        panel: saved[panelId] || panel,
        readings: Object.fromEntries(keys.map((key) => [key, saved[ids[key]] || readingResources[key]])),
    };
}

const interpretationCodes = (interpretation) =>
    (interpretation || [])
        .flatMap((concept) => (concept?.coding || []).map((coding) => `${coding?.system}|${coding?.code}`))
        .sort()
        .join(',');

const isBmiCategorised = (bmiReading) =>
    (bmiReading?.interpretation || []).some((concept) => (concept?.coding || []).some((coding) => coding?.system === VITALS_INTERPRETATION_SYSTEM && coding?.code !== 'orthostatic-drop'));

const PROVENANCE_ACTIVITY_DISPLAY = { UPDATE: 'revise', CREATE: 'create', DELETE: 'delete' };

// One Provenance per change in an edit. `revisionOf` is the resource as read, for UPDATE and DELETE.
function buildProvenance({ targetId, activity, revisionOf, reason, recorded }) {
    const provenance = {
        resourceType: 'Provenance',
        // Native { id, resourceType } form: Aidbox's Provenance target= search does not match a stored string reference.
        target: [observationReference(targetId)],
        recorded,
        agent: [{ who: clone(useAuthStore().aidboxPersonReference || {}) }],
        activity: { coding: [{ system: DATA_OPERATION_SYSTEM, code: activity, display: PROVENANCE_ACTIVITY_DISPLAY[activity] }] },
    };
    if (revisionOf) provenance.entity = [{ role: 'revision', what: { reference: `Observation/${revisionOf.id}/_history/${revisionOf.meta.versionId}` } }];
    if (!isBlank(reason)) provenance.reason = [{ text: reason.trim() }];
    return provenance;
}

const sameTime = (a, b) => new Date(a).getTime() === new Date(b).getTime();
const ifMatch = (resource) => `W/"${resource.meta.versionId}"`;

function requireVersion(resource) {
    if (!resource?.meta?.versionId) throw new Error(`Vitals: ${resource?.id} has no version to edit against`);
}

// Aidbox answers a stale ifMatch inside a transaction with a 400 whose OperationOutcome says "Response status is 412".
function isConflict(error) {
    const status = error?.response?.status;
    if (status === 409 || status === 412) return true;
    if (status !== 400) return false;
    try {
        return JSON.stringify(error.response.data ?? '').includes('Response status is 412');
    } catch {
        return false;
    }
}

// An existing reading rebuilt for the edited values: its value, interpretation, orthostatic sibling and time taken.
function amendReading(key, original, values, { age, takenAt, timeChanged, recategorise, previousSiblingId, siblingId, siblingIds }) {
    const vital = VITALS_BY_KEY[key];
    const updated = clone(original);

    if (vital.kind === 'bloodPressure') {
        ['systolic', 'diastolic'].forEach((part) => {
            const component = (updated.component || []).find((item) => item?.code?.coding?.some((coding) => coding?.code === BP_COMPONENT_CODINGS[part].code));
            if (component && component.value?.Quantity?.value !== values[key][part]) {
                component.value = { Quantity: { ...(component.value?.Quantity || quantity(vital, values[key][part]).Quantity), value: values[key][part] } };
            }
        });
    } else if (updated.value?.Quantity?.value !== values[key]) {
        updated.value = { Quantity: { ...(updated.value?.Quantity || quantity(vital, values[key]).Quantity), value: values[key] } };
    }

    if (recategorise && (key === 'bmi' || vital.kind === 'bloodPressure')) {
        const interpretation = interpretationForReading(key, values, age);
        if (interpretationCodes(interpretation) !== interpretationCodes(original.interpretation)) {
            if (interpretation.length) {
                updated.interpretation = interpretation;
            } else {
                delete updated.interpretation;
            }
        }
    }

    if (key === 'bpStanding' && previousSiblingId !== siblingId) {
        const derivedFrom = (updated.derivedFrom || []).filter((reference) => !siblingIds.includes(referenceId(reference)));
        if (siblingId) derivedFrom.push(observationReference(siblingId));
        if (derivedFrom.length) {
            updated.derivedFrom = derivedFrom;
        } else {
            delete updated.derivedFrom;
        }
    }

    if (timeChanged) updated.effective = { dateTime: takenAt };
    return updated;
}

// The set as read, the edited measured values (with calculated vitals derived), the time taken and the age for BMI.
// Refuses, before anything is built, an edit that leaves no reading or carries an invalid time.
function resolveEdit(set, { readings, effectiveDateTime }, patient) {
    const panel = toPlain(set?.panel);
    if (!panel?.id) throw new Error('Vitals: a saved set is required to edit');
    const originals = Object.fromEntries(
        Object.entries(set?.readings || {})
            .map(([key, reading]) => [key, toPlain(reading)])
            .filter(([key, reading]) => VITALS_BY_KEY[key] && reading?.id),
    );

    const current = {};
    Object.entries(originals).forEach(([key, reading]) => {
        const value = readValue(key, reading);
        if (value !== undefined) current[key] = value;
    });

    // Calculated vitals are never taken from the caller: they are derived from the measured readings.
    const entered = {};
    VITALS.filter((vital) => !vital.calculatedFrom).forEach((vital) => {
        const value = readings === undefined ? current[vital.key] : normalizeReading(vital.key, readings?.[vital.key]);
        if (value !== undefined) entered[vital.key] = value;
    });
    if (!Object.keys(entered).length) throw new Error('A set needs at least one reading. Delete the set instead');

    if (!isBlank(effectiveDateTime) && Number.isNaN(new Date(effectiveDateTime).getTime())) throw new Error('Vitals: the time taken is not a valid date');
    const timeChanged = !isBlank(effectiveDateTime) && !sameTime(effectiveDateTime, panel.effective?.dateTime);
    const takenAt = timeChanged ? effectiveDateTime : panel.effective?.dateTime;

    // BMI is categorised at the (possibly new) time taken; without a birth date, a BMI categorised at save stays categorised.
    const age = patient?.birthDate ? ageInYears(patient.birthDate, takenAt) : isBmiCategorised(originals.bmi) ? ADULT_BMI_AGE : null;
    return { panel, originals, current, values: withCalculated(entered), timeChanged, takenAt, age };
}

// Which readings are amended, added and cleared, and the set's readings after the edit.
function planReadingChanges({ panel, originals, current, values, timeChanged, takenAt, age }) {
    const ids = Object.fromEntries(Object.entries(originals).map(([key, reading]) => [key, reading.id]));
    VITALS.forEach(({ key }) => {
        if (values[key] !== undefined && !ids[key]) ids[key] = uuidv4();
    });

    const previousComparison = orthostaticComparison(current);
    const comparedWith = orthostaticComparison(values);
    const siblingContext = {
        previousSiblingId: previousComparison ? originals[previousComparison].id : null,
        siblingId: comparedWith ? ids[comparedWith] : null,
        siblingIds: ['bpLying', 'bpSitting'].map((sibling) => ids[sibling]).filter(Boolean),
    };

    const links = {};
    if (panel.encounter) links.encounter = panel.encounter;
    if (panel.derivedFrom) links.derivedFrom = panel.derivedFrom;
    const performer = clone(useAuthStore().aidboxPersonReference || {});
    const baseResource = (id, profile) => baseObservation(id, profile, { subject: clone(panel.subject), effectiveDateTime: takenAt, performer, links });

    // Only readings whose inputs changed are re-categorised, so an untouched reading is never rewritten.
    const changedKeys = new Set(VITALS.map(({ key }) => key).filter((key) => JSON.stringify(values[key]) !== JSON.stringify(current[key])));
    const needsRecategorising = (key) => {
        if (key === 'bmi') return timeChanged || ['height', 'weight', 'bmi'].some((input) => changedKeys.has(input));
        if (key === 'bpStanding') return ['bpStanding', 'bpLying', 'bpSitting'].some((input) => changedKeys.has(input));
        return changedKeys.has(key);
    };

    const changes = { amended: [], added: [], cleared: [], result: {} };
    VITALS.forEach(({ key }) => {
        const original = originals[key];
        if (!original && values[key] === undefined) return;
        if (!original) {
            changes.result[key] = buildReading(key, values, { age, baseResource, ids });
            changes.added.push(changes.result[key]);
            return;
        }
        if (values[key] === undefined) {
            requireVersion(original);
            changes.cleared.push(original);
            return;
        }
        const updated = amendReading(key, original, values, { age, takenAt, timeChanged, recategorise: needsRecategorising(key), ...siblingContext });
        if (!createPatch(original, updated).length) {
            changes.result[key] = original;
            return;
        }
        requireVersion(original);
        updated.status = 'amended';
        changes.amended.push({ original, ops: createPatch(original, updated) });
        changes.result[key] = updated;
    });
    return changes;
}

// The panel's json-patch for new membership, a new time taken or a changed note, and the panel as it will read.
function panelChanges(panel, result, { timeChanged, takenAt, note }) {
    const updatedPanel = clone(panel);
    const ops = [];
    const memberIds = Object.values(result).map((reading) => reading.id);
    const previousMemberIds = (panel.hasMember || []).map(referenceId);
    if (memberIds.length !== previousMemberIds.length || memberIds.some((id) => !previousMemberIds.includes(id))) {
        updatedPanel.hasMember = memberIds.map(observationReference);
        ops.push({ op: 'replace', path: '/hasMember', value: updatedPanel.hasMember });
    }
    if (timeChanged) {
        updatedPanel.effective = { dateTime: takenAt };
        ops.push({ op: 'replace', path: '/effective', value: updatedPanel.effective });
    }
    const currentNote = panel.note?.[0]?.text || '';
    const nextNote = note === undefined || note === null ? currentNote : String(note).trim();
    if (nextNote !== currentNote) {
        if (nextNote) {
            updatedPanel.note = [{ text: nextNote }];
            ops.push({ op: panel.note ? 'replace' : 'add', path: '/note', value: updatedPanel.note });
        } else {
            delete updatedPanel.note;
            ops.push({ op: 'remove', path: '/note' });
        }
    }
    if (ops.length) {
        requireVersion(panel);
        updatedPanel.status = 'amended';
        ops.push({ op: 'replace', path: '/status', value: 'amended' });
    }
    return { ops, updatedPanel };
}

// The transaction's entries: every write with ifMatch where it replaces a version, then a Provenance per change.
function editEntries(panel, panelOps, { amended, added, cleared }, reason) {
    const recorded = new Date().toISOString();
    const entry = [];
    const provenances = [];
    if (panelOps.length) {
        entry.push({ request: { method: 'PATCH', url: `/Observation/${panel.id}?_method=json-patch`, ifMatch: ifMatch(panel) }, resource: panelOps });
        provenances.push(buildProvenance({ targetId: panel.id, activity: 'UPDATE', revisionOf: panel, reason, recorded }));
    }
    amended.forEach(({ original, ops }) => {
        entry.push({ request: { method: 'PATCH', url: `/Observation/${original.id}?_method=json-patch`, ifMatch: ifMatch(original) }, resource: ops });
        provenances.push(buildProvenance({ targetId: original.id, activity: 'UPDATE', revisionOf: original, reason, recorded }));
    });
    added.forEach((reading) => {
        entry.push({ request: { method: 'POST', url: '/Observation' }, resource: clone(reading) });
        provenances.push(buildProvenance({ targetId: reading.id, activity: 'CREATE', reason, recorded }));
    });
    cleared.forEach((original) => {
        entry.push({ request: { method: 'DELETE', url: `/Observation/${original.id}`, ifMatch: ifMatch(original) } });
        provenances.push(buildProvenance({ targetId: panel.id, activity: 'DELETE', revisionOf: original, reason, recorded }));
    });
    entry.push(...provenances.map((resource) => ({ request: { method: 'POST', url: '/Provenance' }, resource })));
    return entry;
}

/**
 * Edits a saved set as a whole in ONE transaction. `readings` is the complete intended measured set, so a
 * reading left out is cleared and a new one is added. Every changed resource is written with ifMatch on its
 * version as read, so a stale version fails the whole edit and nothing is written.
 */
async function editSet(set, { readings, effectiveDateTime, note } = {}, { reason, patient } = {}) {
    const edit = resolveEdit(set, { readings, effectiveDateTime }, patient);
    const { panel } = edit;
    const { amended, added, cleared, result } = planReadingChanges(edit);
    const { ops: panelOps, updatedPanel } = panelChanges(panel, result, { timeChanged: edit.timeChanged, takenAt: edit.takenAt, note });
    // Nothing changed, nothing written: revision stays put, so no surface reloads for it.
    if (!panelOps.length && !amended.length && !added.length && !cleared.length) return set;

    const entry = editEntries(panel, panelOps, { amended, added, cleared }, reason);
    let response;
    try {
        response = await $httpFhirApi.post('/', transactionBundle(entry));
    } catch (error) {
        if (isConflict(error)) throw new VitalsConflictError(panel.id, error);
        console.error('[useVitalsFlow] editSet transaction failed', {
            panelId: panel.id,
            amendedIds: amended.map(({ original }) => original.id),
            addedIds: added.map(({ id }) => id),
            clearedIds: cleared.map(({ id }) => id),
            error,
        });
        throw error;
    }

    const saved = savedById(response);
    revision.value++;
    return {
        panel: saved[panel.id] || updatedPanel,
        readings: Object.fromEntries(Object.entries(result).map(([key, reading]) => [key, saved[reading.id] || reading])),
    };
}

async function deleteSet(set) {
    const observationIds = [set?.panel?.id, ...Object.values(set?.readings || {}).map((reading) => reading?.id)].filter(Boolean);
    if (!observationIds.length) throw new Error('Vitals: nothing to delete');

    try {
        const provenanceResponse = await $httpFhirApi.get(`/Provenance?target=${observationIds.join(',')}`);
        const provenanceIds = [
            ...new Set(
                (provenanceResponse?.data || [])
                    .map(toPlain)
                    .filter((resource) => resource?.id && (!resource.resourceType || resource.resourceType === 'Provenance'))
                    .map((resource) => resource.id),
            ),
        ];

        const entry = [
            ...observationIds.map((id) => ({ request: { method: 'DELETE', url: `/Observation/${id}` } })),
            ...provenanceIds.map((id) => ({ request: { method: 'DELETE', url: `/Provenance/${id}` } })),
        ];
        await $httpFhirApi.post('/', transactionBundle(entry));
    } catch (error) {
        console.error('[useVitalsFlow] deleteSet failed', { observationIds, error });
        throw error;
    }
    revision.value++;
}

// Full-precision bounds only: Aidbox matches later readings on the same day for a day-precision date=lt.
function toDateTimeBound(value, label) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) throw new Error(`Vitals: ${label} is not a valid date`);
    return date.toISOString();
}

// With a positive limit, only the newest `limit` sets are read: one page of `_count` panels (FHIR counts matches, not includes).
// With questionnaireResponseId, only the sets entered on that note.
async function fetchHistory(patientId, { from, to, limit, questionnaireResponseId } = {}) {
    if (!patientId) throw new Error('Vitals: a patient is required to read history');
    const hasLimit = Number.isFinite(limit) && limit > 0;

    let url = `/Observation?subject=${patientId}&category=vital-signs&code=${PANEL_CODING.system}|${PANEL_CODING.code}&_include=Observation:has-member&_sort=-date`;
    if (from) url += `&date=ge${toDateTimeBound(from, 'from')}`;
    if (to) url += `&date=lt${toDateTimeBound(to, 'to')}`;
    if (questionnaireResponseId) url += `&derived-from=${questionnaireResponseId}`;
    if (hasLimit) url += `&_count=${limit}`;

    try {
        const resources = [];
        let response = await $httpFhirApi.get(url);
        resources.push(...(response?.data || []));
        for (let page = 2; !hasLimit && response?.originalData?.link?.some((link) => link?.relation === 'next') && response?.data?.length; page++) {
            // eslint-disable-next-line no-await-in-loop
            response = await $httpFhirApi.get(`${url}&_page=${page}`);
            resources.push(...(response?.data || []));
        }

        const observations = resources.map(toPlain).filter((resource) => resource?.id && isVitalSign(resource));
        const byId = new Map(observations.map((observation) => [observation.id, observation]));
        const panels = [...new Map(observations.filter(isPanel).map((panel) => [panel.id, panel])).values()];

        const sets = panels.map((panel) => {
            const readings = {};
            (panel.hasMember || []).forEach((reference) => {
                const member = byId.get(referenceId(reference));
                const key = member && vitalKeyFor(member);
                if (key) readings[key] = member;
            });
            return { panel, readings, provenanceByReadingId: {}, panelProvenance: [] };
        });
        const takenAt = (set) => new Date(set.panel.effective?.dateTime || 0).getTime();
        sets.sort((a, b) => takenAt(b) - takenAt(a));
        if (hasLimit) sets.splice(limit);

        // One Provenance search covers every amended reading and every amended panel, plus every member of an
        // amended panel, so a reading added by a later edit carries its CREATE Provenance.
        const amendedIds = [
            ...new Set(
                sets.flatMap((set) => {
                    const members = Object.values(set.readings);
                    if (set.panel.status === 'amended') return [set.panel.id, ...members.map((reading) => reading.id)];
                    return members.filter((reading) => reading.status === 'amended').map((reading) => reading.id);
                }),
            ),
        ];
        if (amendedIds.length) {
            const provenanceResponse = await $httpFhirApi.get(`/Provenance?target=${amendedIds.join(',')}`);
            const provenanceByTarget = {};
            (provenanceResponse?.data || []).map(toPlain).forEach((provenance) => {
                (provenance?.target || []).forEach((target) => {
                    const id = referenceId(target);
                    if (id) (provenanceByTarget[id] ||= []).push(provenance);
                });
            });
            sets.forEach((set) => {
                if (set.panel.status === 'amended') set.panelProvenance = provenanceByTarget[set.panel.id] || [];
                Object.values(set.readings).forEach((reading) => {
                    if (provenanceByTarget[reading.id]) set.provenanceByReadingId[reading.id] = provenanceByTarget[reading.id];
                });
            });
        }

        return sets;
    } catch (error) {
        console.error('[useVitalsFlow] fetchHistory failed', { patientId, from, to, limit, questionnaireResponseId, error });
        throw error;
    }
}

/**
 * A deleted note keeps its vitals: for each vital-signs Observation derived from the form response,
 * append a PATCH that removes encounter and turns that derivedFrom entry into { display: 'Deleted note' }.
 * Never deletes anything.
 */
async function detachVitalsFromNote({ questionnaireResponseId, patientId } = {}, transaction = []) {
    const txn = transaction || [];
    if (!questionnaireResponseId) return txn;

    let response;
    try {
        response = await $httpFhirApi.get(`/Observation?${patientId ? `subject=${patientId}&` : ''}category=vital-signs&derived-from=${questionnaireResponseId}`);
    } catch (error) {
        console.error('[useVitalsFlow] detachVitalsFromNote lookup failed', { questionnaireResponseId, patientId, error });
        throw error;
    }

    const observations = (Array.isArray(response?.data) ? response.data : []).map(toPlain).filter((observation) => observation?.id && isVitalSign(observation));
    observations.forEach((observation) => {
        const ops = [];
        (observation.derivedFrom || []).forEach((reference, index) => {
            if (referenceId(reference) === questionnaireResponseId) ops.push({ op: 'replace', path: `/derivedFrom/${index}`, value: { display: 'Deleted note' } });
        });
        if (!ops.length) return;
        if (observation.encounter) ops.unshift({ op: 'remove', path: '/encounter' });
        txn.push({ request: { method: 'PATCH', url: `/Observation/${observation.id}?_method=json-patch` }, resource: ops });
    });

    return txn;
}

// One version of a reading, from a Provenance `_history` reference. A purged version (404 / 410) resolves null.
async function fetchReadingVersion(reference) {
    const path = reference?.startsWith('/') ? reference : `/${reference}`;
    try {
        const response = await $httpFhirApi.get(path);
        return toPlain(response?.data) || null;
    } catch (error) {
        console.error('[useVitalsFlow] fetchReadingVersion failed', { reference, error });
        if ([404, 410].includes(error?.response?.status)) return null;
        throw error;
    }
}

// The notes, among the given QuestionnaireResponse ids, that no vitals set was entered on. One search for them all.
async function notesWithoutVitals(questionnaireResponseIds) {
    const ids = (questionnaireResponseIds || []).filter(Boolean);
    if (!ids.length) return [];

    let response;
    try {
        response = await $httpFhirApi.get(
            `/Observation?category=vital-signs&code=${PANEL_CODING.system}|${PANEL_CODING.code}&derived-from=${ids.join(',')}&_elements=derivedFrom&_count=${NOTE_PANELS_FETCH_COUNT}`,
        );
    } catch (error) {
        console.error('[useVitalsFlow] notesWithoutVitals failed', { questionnaireResponseIds: ids, error });
        throw error;
    }

    const withVitals = new Set((Array.isArray(response?.data) ? response.data : []).map(toPlain).flatMap((panel) => (panel?.derivedFrom || []).map(referenceId)));
    return ids.filter((id) => !withVitals.has(id));
}

export function useVitalsFlow() {
    return { createSet, editSet, deleteSet, fetchHistory, fetchReadingVersion, notesWithoutVitals, detachVitalsFromNote, isVitalSign, revision: readonly(revision) };
}
